/* eslint-disable no-unused-vars */
import React from "react";
import cx from "classnames";
import { arrayMove } from "@dnd-kit/sortable";
import { Column } from "react-base-table";

// eslint-disable-next-line import/no-cycle
import BodyCell from "./components/BodyCell";

const CLASS_PREFIX = "orion-data-grid";

/* istanbul ignore next */
// This function is being used in DnD feature so ignoring it. We don't test DnD in jestdom
const findIndexByKey = (items, key) =>
  items.findIndex(elem => elem.key === key);

const makeCompatibleColumn = (
  {
    alignItems,
    key,
    className,
    headerClassName,
    style,
    title,
    dataKey,
    dataGetter,
    textAlign,
    flexGrow,
    flexShrink,
    width,
    minWidth,
    maxWidth,
    fixed,
    hidden,
    resizable,
    sortable,
    fixable,
    togglable,
    draggable,
    cellRenderer,
    headerRenderer,
    truncateText,
    tooltip,
    children,
    exportable,
    getCsvValue,
    csvTitle,
    isLoading,
    isError
  },
  responsive
) => ({
  key,
  className: cx(
    alignItems && `${CLASS_PREFIX}__row-cell--valign-${alignItems}`,
    truncateText && `${CLASS_PREFIX}__row-cell--truncate-text`,
    className
  ),
  headerClassName: cx(
    resizable && `${CLASS_PREFIX}__header-cell--resizable`,
    headerClassName
  ),
  style,
  title,
  dataKey,
  dataGetter,
  align: textAlign,
  flexGrow,
  flexShrink,
  width: /* istanbul ignore next */ responsive ? 0 : width,
  minWidth,
  maxWidth,
  frozen: fixed,
  hidden,
  resizable,
  sortable: isLoading || isError ? false : sortable,
  fixable,
  togglable,
  draggable,
  cellRenderer: cellRenderer
    ? ({ cellData, _, __, ___, rowData, rowIndex }) =>
        cellRenderer(cellData, dataKey, rowIndex, rowData)
    : BodyCell,
  headerRenderer,
  truncateText,
  tooltip,
  children,
  exportable,
  getCsvValue,
  csvTitle,
  isLoading,
  isError
});

const flattenColumns = (columns, responsive, parent) => {
  return columns
    .map(column => {
      /* istanbul ignore else */
      if (!column.children) {
        const compatibleColumn = makeCompatibleColumn(column, responsive);
        return { ...compatibleColumn, parents: [...(parent || [])] };
      }

      return flattenColumns(column.children, responsive, [
        ...(parent || []),
        column.title
      ]);
    })
    .flat(3);
};

const extractColumns = (columns, parent) => {
  /* istanbul ignore next */
  // This function is used in drag and drop and we are not testing that
  // Because drag/drop things cannot be tested in env like jsdom without
  // actual layout rendering
  return columns
    .map(column => {
      if (!column.children) {
        return { ...column, parents: [...(parent || [])] };
      }

      return extractColumns(column.children, [...(parent || []), column.title]);
    })
    .flat(3);
};

const rebuildColumnHirarchy = (columns, colParents, column) => {
  let leaf;

  function makeEmptyChildren(defs, parents, idx = 0) {
    const title = parents[idx];
    const existing = defs.find(elem => elem.title === title);

    /* istanbul ignore else */
    if (idx === parents.length) {
      leaf = defs;
      return;
    }

    if (!existing) {
      defs.push({ title, key: title, children: [] });
      makeEmptyChildren(defs[defs.length - 1].children, parents, idx + 1);
    } else {
      makeEmptyChildren(existing.children, parents, idx + 1);
    }
  }

  makeEmptyChildren(columns, colParents);

  leaf.push(column);
};

const rebuildColumnsDef = columns => {
  const columnsDef = [];

  columns.forEach(column => {
    /* istanbul ignore else */
    // The else condition never hits but may act weirdly sometimes
    if (column.parents) {
      rebuildColumnHirarchy(columnsDef, column.parents, column);
    } else {
      columnsDef.push(column);
    }
  });

  return columnsDef;
};

const getMaxHeaderCount = columns => {
  let max = 1;

  columns.forEach(({ parents }) => {
    if (parents && parents.length + 1 > max) {
      max = parents.length + 1;
    }
  });

  return max;
};

const buildHeaderRow = cells => {
  const result = [];

  for (let i = 0; i < cells.length; i++) {
    const { title, width } = cells[i];

    if (i === 0) {
      result.push({ title, width });
    } else {
      const lastResultItem = result[result.length - 1];
      if (lastResultItem.title === title) {
        lastResultItem.width += width;
      } else {
        result.push({ title, width });
      }
    }
  }

  return result;
};

/* istanbul ignore next */
const shuffleDndItems = (columns, active, over) => {
  const oldIndex = findIndexByKey(columns, active.id);
  const newIndex = findIndexByKey(columns, over.id);
  return arrayMove(columns, oldIndex, newIndex);
};

const isFunction = func => typeof func === "function";

const isString = str => typeof str === "string";

const isObject = obj => typeof obj === "object" && obj !== null;

const getSelectionColumn = (Component, multiSelect, fixed) => ({
  title: multiSelect ? "__checkbox_selection__" : "",
  width: 40,
  align: "center",
  flexShrink: 0,
  frozen: fixed ? Column.FrozenDirection.LEFT : false,
  key: "__selection__",
  cellRenderer: props => <Component {...props} />,
  parents: []
});

const getTooltipPropsFromCallback = cbResult => {
  /* istanbul ignore else */
  if (isString(cbResult) || isFunction(cbResult)) {
    return { text: cbResult };
  }

  /* istanbul ignore else */
  if (typeof cbResult === "number") {
    return { text: String(cbResult) };
  }

  /* istanbul ignore else */
  if (isObject(cbResult)) {
    return cbResult;
  }
  /* istanbul ignore next */
  // Probably we never run into null condition so ignoring it.
  // Tooltip will itself break in that case so no need to test it.
  return null;
};

const setColumnsInitialWidth = (cols, width, isSelectable) => {
  const effectiveWidth = isSelectable ? width - 50 : width;
  const totalFlex = cols
    .filter(c => !c.hidden)
    .map(c => c.flexGrow || 1)
    .reduce((curr, total) => curr + total, 0);

  return cols.map(c => {
    const newCol = { ...c };
    newCol.width = (effectiveWidth / totalFlex) * (c.flexGrow || 1);
    return newCol;
  });
};

const getStoredColumnsDiff = (tableId, columns) => {
  const configKey = `dg-conf-${tableId}`;
  let diffExists = false;

  try {
    const configJson = localStorage.getItem(configKey);

    if (configJson) {
      const config = JSON.parse(configJson);

      diffExists =
        columns.filter(column => {
          const storedCol = config[column.key];
          if (
            !storedCol ||
            (storedCol &&
              (storedCol.hidden !== column.hidden ||
                storedCol.frozen !== column.frozen ||
                storedCol.width !== column.width))
          ) {
            return true;
          }

          return false;
        }).length > 0;
    }

    return true;
  } catch (e) {
    diffExists = true;
  }

  return diffExists;
};

const storeTableConfigInLocalStorage = (
  tableId,
  enablePersistConfig,
  columns
) => {
  if (
    tableId &&
    enablePersistConfig &&
    getStoredColumnsDiff(tableId, columns)
  ) {
    const columnsObj = columns.reduce((acc, curr) => {
      acc[curr.key] = {
        width: curr.width,
        hidden: curr.hidden,
        frozen: curr.frozen
      };

      return acc;
    }, {});

    setTimeout(() => {
      localStorage.setItem(`dg-conf-${tableId}`, JSON.stringify(columnsObj));
    }, 0);
  }
};

const hydradeColumnsFromLocalStorage = (
  columns,
  tableId,
  enablePersistConfig
) => {
  if (!enablePersistConfig || !tableId) {
    return columns;
  }

  const storageKey = `dg-conf-${tableId}`;

  try {
    const storedJson = localStorage.getItem(storageKey);

    if (storedJson) {
      const storedCols = JSON.parse(storedJson);
      const storedColKeys = Object.keys(storedCols);
      const originalColKeys = columns.map(({ key }) => key);
      if (
        storedColKeys.length !== originalColKeys.length ||
        (storedColKeys.length === originalColKeys.length &&
          !storedColKeys.every(s => originalColKeys.includes(s)))
      ) {
        localStorage.removeItem(storageKey);
        return columns;
      }

      return storedColKeys.map(sck => {
        const storedCol = storedCols[sck];
        const correspondingCol = columns.find(c => c.key === sck);

        return {
          ...correspondingCol,
          width: storedCol.width,
          hidden: storedCol.hidden,
          frozen: storedCol.frozen
        };
      });
    }

    return columns;
  } catch (error) {
    return columns;
  }
};

const shouldReallySetInitialWidth = (columns, tableId, enablePersistConfig) => {
  if (!enablePersistConfig || !tableId) {
    return true;
  }

  const storageKey = `dg-conf-${tableId}`;

  try {
    const storedJson = localStorage.getItem(storageKey);

    if (storedJson) {
      const storedCols = JSON.parse(storedJson);
      const storedColKeys = Object.keys(storedCols);
      const originalColKeys = columns.map(({ key }) => key);

      if (
        storedColKeys.length !== originalColKeys.length ||
        (storedColKeys.length === originalColKeys.length &&
          !storedColKeys.every(s => storedColKeys.includes(s)))
      ) {
        return true;
      }

      return false;
    }

    return true;
  } catch (error) {
    return true;
  }
};

export {
  CLASS_PREFIX,
  buildHeaderRow,
  findIndexByKey,
  getMaxHeaderCount,
  flattenColumns,
  extractColumns,
  rebuildColumnsDef,
  shuffleDndItems,
  getSelectionColumn,
  isFunction,
  isString,
  isObject,
  getTooltipPropsFromCallback,
  setColumnsInitialWidth,
  storeTableConfigInLocalStorage,
  hydradeColumnsFromLocalStorage,
  shouldReallySetInitialWidth
};
